// src/components/admin/AdsManager.tsx
/**
 * 広告管理用画面
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ad } from '../../types/index';
import {
  fetchAllAds,
  addAd,
  updateExistingAd,
  deleteAd,
  validateAd
} from '../../services/adsService';
import { logger } from '../../utils/logger';

// AdCardの定義。
// AdManagerからのみ利用のためここで定義
interface AdCardProps {
  ad: Ad;
  isEditing: boolean;
  current: Partial<Ad>;
  onChange: (id: string, field: keyof Ad, value: string | number) => void;
  onSave: (id: string) => void;
  onCancel: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const AdCard: React.FC<AdCardProps> = React.memo(({
  ad,
  isEditing,
  current,
  onChange,
  onSave,
  onCancel,
  onEdit,
  onDelete
}) => {
  return (
    <div className="bg-yellow-50 rounded-2xl shadow-lg group hover:-translate-y-2 transition-all relative">
      {/* 編集モードでない場合は AdsSection と同じ表示 */}
      {!isEditing ? (
        <a href={ad.url} target="_blank" rel="noopener noreferrer" className="block p-6 h-full flex flex-col">
          <h3 className="text-xl font-bold text-gray-800 mb-2">{ad.title}</h3>

          {ad.image_url && (
            <img
              src={ad.image_url}
              alt={ad.title}
              className="w-full h-32 object-cover rounded-md mb-4"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = `https://placehold.co/400x128/cccccc/ffffff?text=No Image`;
              }}
            />
          )}

          <div className="flex-grow"></div>

          <div className="flex items-center justify-between mt-auto">
            <p className="text-sm text-yellow-800 bg-yellow-100 px-3 py-1 rounded-full font-medium">
              {ad.category}
            </p>
            {ad.authorNickname && <p className="text-gray-500 text-xs">作成者: {ad.authorNickname}</p>}
          </div>
        </a>
      ) : (
        // 編集モードの場合はフォームを表示
        <div className="p-6 h-full flex flex-col bg-white border border-blue-300 rounded-2xl">
          <h3 className="text-xl font-bold text-gray-800 mb-2">広告を編集</h3>
          {(['title', 'category', 'authorNickname', 'url', 'image_url', 'description'] as Array<keyof Ad>).map(field => (
            <div key={field} className="mb-2">
              <label className="block text-sm font-bold text-gray-700">{field}</label>
              <input
                className="border p-2 rounded w-full text-gray-800"
                value={current[field]?.toString() ?? ''}
                onChange={(e) => onChange(ad.id!, field, e.target.value)}
              />
            </div>
          ))}
          <div className="mb-2">
            <label className="block text-sm font-bold text-gray-700">対象年齢</label>
            <div className="flex gap-2 items-center">
              <input
                type="number"
                className="border p-2 rounded w-20 text-gray-800"
                value={current.age_from}
                onChange={(e) => onChange(ad.id!, 'age_from', parseInt(e.target.value) || '')}
              />
              〜
              <input
                type="number"
                className="border p-2 rounded w-20 text-gray-800"
                value={current.age_to}
                onChange={(e) => onChange(ad.id!, 'age_to', parseInt(e.target.value) || '')}
              />
            </div>
          </div>
        </div>
      )}

      {/* 操作ボタンを右下に配置 */}
      <div className="absolute bottom-4 right-4 flex gap-2">
        {isEditing ? (
          <>
            <button onClick={() => onSave(ad.id!)} className="bg-blue-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-blue-600 transition">保存</button>
            <button onClick={() => onCancel(ad.id!)} className="bg-gray-300 text-gray-800 px-3 py-1 rounded-lg text-sm hover:bg-gray-400 transition">取消</button>
          </>
        ) : (
          <>
            <button onClick={() => onEdit(ad.id!)} className="bg-green-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-green-600 transition">編集</button>
            <button onClick={() => onDelete(ad.id!)} className="bg-red-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-red-600 transition">削除</button>
          </>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  if (prevProps.isEditing !== nextProps.isEditing) return false;
  if (prevProps.ad.id !== nextProps.ad.id) return false;
  if (prevProps.isEditing) {
    const keys: (keyof Ad)[] = ['title', 'category', 'authorNickname', 'url', 'age_from', 'age_to', 'image_url', 'description'];
    for (const key of keys) {
      if (prevProps.current[key] !== nextProps.current[key]) return false;
    }
  }
  return true;
});


const AdManager: React.FC = () => {
  const navigate = useNavigate();

  const [allAds, setAllAds] = useState<Ad[]>([]); // 全ての広告を保持
  const [filteredAds, setFilteredAds] = useState<Ad[]>([]); // フィルタリングされた広告を表示
  const [ageFilter, setAgeFilter] = useState<string>(''); // 年齢フィルターの入力値
  const [editStates, setEditStates] = useState<{ [key: string]: Partial<Ad> }>({});
  const [newAd, setNewAd] = useState<Ad>({
    title: '', category: '広告', authorNickname: 'オーナー', url: '', age_from: 0, age_to: 20,
    image_url: '', description: '',
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);


  // 広告データをロードする関数
  const loadAds = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await fetchAllAds();
      setAllAds(data);
      setFilteredAds(data); // 初期表示は全広告
    } catch (err: any) {
      logger.error('Failed to fetch ads:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAds();
  }, [loadAds]);

  // 年齢フィルターの適用
  useEffect(() => {
    if (ageFilter === '') {
      setFilteredAds(allAds);
      return;
    }
    const age = parseInt(ageFilter);
    if (isNaN(age)) {
      setFilteredAds(allAds); // 数字でない場合はフィルターしない
      return;
    }

    const filtered = allAds.filter(ad => {
      // adのage_fromとage_toが数値であることを確認
      const adAgeFrom = typeof ad.age_from === 'string' ? parseInt(ad.age_from) : ad.age_from;
      const adAgeTo = typeof ad.age_to === 'string' ? parseInt(ad.age_to) : ad.age_to;

      // どちらかが未定義または無効な数値の場合はフィルター対象外
      if (isNaN(adAgeFrom as number) || isNaN(adAgeTo as number)) {
        return false;
      }
      return age >= (adAgeFrom as number) && age <= (adAgeTo as number);
    });
    setFilteredAds(filtered);
  }, [ageFilter, allAds]);


  // 編集中の値変更ハンドラ
  const handleEditChange = (id: string, field: keyof Ad, value: string | number) => {
    setEditStates(prev => ({
      ...prev,
      [id]: {
        ...(prev[id] || allAds.find(ad => ad.id === id) || {}),
        [field]: value
      }
    }));
  };

  // 編集保存ハンドラ
  const handleEditSave = async (id: string) => {
    const updated = editStates[id];

    const adToValidate: Partial<Ad> = {
      ...(allAds.find(ad => ad.id === id) || {}),
      ...updated,
    };

    const error = validateAd(adToValidate);
    if (error) {
      logger.error('Validation error:', error);
      alert(error); // エラーメッセージをアラートで表示
      return;
    }

    try {
      const { id: _, ...dataToUpdate } = updated;
      await updateExistingAd(id, dataToUpdate);
      await loadAds(); // 更新後、再フェッチして状態を最新にする
      setEditStates(prev => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    } catch (err: any) {
      logger.error('Failed to update ad:', err);
      alert('広告の更新に失敗しました: ' + err.message);
    }
  };

  // 編集キャンセルハンドラ
  const handleEditCancel = (id: string) => {
    setEditStates(prev => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  // 削除ハンドラ
  const handleDelete = async (id: string) => {
    if (window.confirm('この広告を本当に削除しますか？')) {
      try {
        await deleteAd(id);
        await loadAds(); // 削除後、再フェッチして状態を最新にする
      } catch (err: any) {
        logger.error('Failed to delete ad:', err);
        alert('広告の削除に失敗しました: ' + err.message);
      }
    }
  };

  // 新規広告保存ハンドラ
  const handleNewAdSave = async () => {
    const error = validateAd(newAd);
    if (error) {
      logger.error('Validation error:', error);
      alert(error); // エラーメッセージをアラートで表示
      return;
    }

    try {
      const { id, ...dataToAdd } = newAd;
      await addAd(dataToAdd);
      await loadAds(); // 新規追加後、再フェッチして最新のデータを取得
      setNewAd({
        title: '', category: '広告', authorNickname: 'オーナー', url: '', age_from: 0, age_to: 20
        , image_url: '', description: ''
      });
    } catch (err: any) {
      logger.error('Failed to add new ad:', err);
      alert('新しい広告の追加に失敗しました: ' + err.message);
    }
  };

  if (isLoading) {
    return <p className="text-center py-4 text-gray-500">広告データを読み込み中...</p>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h2 className="text-3xl font-extrabold text-center text-gradient from-sky-500 to-cyan-400 mb-8">
        広告管理ダッシュボード
      </h2>

      {/* 年齢フィルター */}
      <div className="mb-6 p-4 bg-white rounded-lg shadow-md flex items-center">
        <label htmlFor="ageFilter" className="mr-2 font-bold text-gray-700">年齢でフィルター:</label>
        <input
          type="number"
          id="ageFilter"
          className="border p-2 rounded w-40 text-gray-800"
          value={ageFilter}
          onChange={(e) => setAgeFilter(e.target.value)}
          placeholder="年齢を入力"
        />
      </div>

      <div className={`grid grid-cols-1 ${filteredAds.length > 1 ? 'sm:grid-cols-2 lg:grid-cols-3' : ''} gap-6 mb-12`}>
        {filteredAds.map((ad: Ad) => {
          const isEditing = !!editStates[ad.id!];
          const current = isEditing ? editStates[ad.id!] : ad;

          return (
            <AdCard
              key={ad.id}
              ad={ad}
              isEditing={isEditing}
              current={current}
              onChange={handleEditChange}
              onSave={handleEditSave}
              onCancel={handleEditCancel}
              onEdit={(id) => setEditStates(prev => ({ ...prev, [id]: { ...ad } }))}
              onDelete={handleDelete}
            />
          );
        })}
      </div>

      {/* 新規追加フォーム */}
      <div className="bg-orange-50 p-6 rounded-xl shadow-inner border border-orange-200">
        <h3 className="text-xl font-bold text-orange-600 mb-4">新しい広告を追加</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {(['title', 'category', 'authorNickname', 'url', 'image_url', 'description'] as Array<keyof Ad>).map((field) => (
            <div key={field}>
              <label className="text-sm font-bold block">{field}</label>
              <input
                className="border p-2 rounded w-full"
                value={newAd[field]?.toString() ?? ''}
                onChange={(e) => setNewAd(prev => ({ ...prev, [field]: e.target.value }))}
              />
            </div>
          ))}
          <div>
            <label className="text-sm font-bold block">対象年齢</label>
            <div className="flex gap-2 items-center">
              <input
                type="number"
                className="border p-2 rounded w-20"
                value={newAd.age_from}
                onChange={(e) => setNewAd(prev => ({ ...prev, age_from: parseInt(e.target.value) || 0 }))}
              />
              〜
              <input
                type="number"
                className="border p-2 rounded w-20"
                value={newAd.age_to}
                onChange={(e) => setNewAd(prev => ({ ...prev, age_to: parseInt(e.target.value) || 0 }))}
              />
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={handleNewAdSave}
            className="bg-custom-gradient from-orange-400 to-red-500 text-white px-6 py-2 rounded-lg font-bold hover:btn-hover-darken"
          >
            追加する
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdManager;